/* eslint-disable react/no-array-index-key */
import {css} from '@mui/material';
import {ConfigObjectType, serializeJSONToString, useApiQuery} from '@wallaby-ui/core';
import {ICellRendererParams} from 'ag-grid-community';
import {sortBy} from 'lodash';
import * as React from 'react';
import {useMemo} from 'react';

import {HaidiSchema} from '../../types';
import {AG_GRID_COMPATIBILITY} from '../LightTable';
import {useAppSelector} from 'src/js/store_hooks';

type Agens = HaidiSchema.components['schemas']['HaiPathogenDto'];
type Finding = HaidiSchema.components['schemas']['LabResultFindingDto'];
type OwnProps = {
  underlined?: boolean;
  noWrap?: boolean;
  style?: React.CSSProperties;
  snomedLabels: Record<string, string>;
  snomedLabelsLoading: boolean;
};
type Props = ICellRendererParams & OwnProps;

function useLocaleSnomedLabels(config: ConfigObjectType, language?: string): {data: Record<string, string>; isLoading: boolean} {
  const currentLocale = useAppSelector((state) => state.i18n.currentLocale);
  const snomedLabelsUrl = config.service.url.snomedLabels;
  const snomedLabelsParams = {
    language: language ?? currentLocale,
  };
  const {data: localeSnomedLabelsOrNull, isLoading} = useApiQuery([snomedLabelsUrl, serializeJSONToString(snomedLabelsParams)], null, {
    axiosConfig: {
      url: snomedLabelsUrl,
      params: new URLSearchParams(snomedLabelsParams),
    },
    reactQueryOptions: {
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  });
  const localeSnomedLabels = React.useMemo(() => localeSnomedLabelsOrNull ?? {}, [localeSnomedLabelsOrNull]);
  return {data: localeSnomedLabels, isLoading};
}
export function useAgens(config: ConfigObjectType): Agens[] {
  // MERIE has no HAI pathogen list (no `pathogens` URL) - without the guard it would request "undefined".
  const pathogensUrl = config.service.url.pathogens;
  const {data: pathogensData} = useApiQuery<Agens[]>([pathogensUrl], null, {
    axiosConfig: {
      url: pathogensUrl,
    },
    reactQueryOptions: {enabled: Boolean(pathogensUrl)},
  });
  const {data: localeSnomedLabels} = useLocaleSnomedLabels(config);
  const {data: englishSnomedLabels} = useLocaleSnomedLabels(config, 'en');
  const result = React.useMemo(
    () =>
      (pathogensData ?? []).map((v) => ({
        ...v,
        label: localeSnomedLabels?.[v.code] || englishSnomedLabels?.[v.code] || v.label,
      })),
    [pathogensData, localeSnomedLabels, englishSnomedLabels]
  );
  return result;
}
export function useFinding(config: ConfigObjectType): Finding[] {
  const pathogensUrl = `${config.service.url.labsFinding}`;
  const {data: pathogensData} = useApiQuery<Finding[]>([pathogensUrl], null, {
    axiosConfig: {
      url: pathogensUrl,
    },
  });
  const {data: localeSnomedLabels} = useLocaleSnomedLabels(config);
  const {data: englishSnomedLabels} = useLocaleSnomedLabels(config, 'en');
  const result = React.useMemo(
    () =>
      (pathogensData ?? []).map((v) => ({
        ...v,
        label: localeSnomedLabels?.[v.code] || englishSnomedLabels?.[v.code] || v.label,
      })),
    [pathogensData, localeSnomedLabels, englishSnomedLabels]
  );
  return result;
}
export function useAtbList(config: ConfigObjectType): {code: number; label: string}[] {
  const atbListUrl = `${config.service.url.labsAtbList}`;
  const {data: atbCodes} = useApiQuery<number[]>([atbListUrl], null, {
    axiosConfig: {
      url: atbListUrl,
    },
  });
  const {data: localeSnomedLabels} = useLocaleSnomedLabels(config);
  const {data: englishSnomedLabels} = useLocaleSnomedLabels(config, 'en');
  return React.useMemo(
    () =>
      (atbCodes ?? []).map((code) => ({
        code,
        label: localeSnomedLabels?.[code] || englishSnomedLabels?.[code] || String(code),
      })),
    [atbCodes, localeSnomedLabels, englishSnomedLabels]
  );
}
/** NOTE: multiple snomed codes can have the same name,
 * e.g. `{36893000: "Ceftadizim"}` (product with ceftadizim) and `{387200005: "Ceftadizim"}`
 */
export function useSnomedLabels(config: ConfigObjectType): {
  snomedLabels: Record<string, string>;
  snomedLabelsLoading: boolean;
} {
  const {data: localeSnomedLabels, isLoading: localeLoading} = useLocaleSnomedLabels(config);
  const {data: englishSnomedLabels, isLoading: englishLoading} = useLocaleSnomedLabels(config, 'en');
  const agens = useAgens(config);
  const snomedLabels = useMemo(() => {
    // the SNOMED dictionaries can be ~4.7 MB each (tens of thousands of keys). Spreading
    // them (`{...english, ...locale}`) built a third full copy that stayed resident for the whole
    // session, just to add the handful of `agens` labels. Instead we precompute only the small
    // `agens` overlay and resolve labels lazily through a Proxy, preserving the original lookup
    // precedence (locale > english > agens fallback) and the `||`-style empty-value behaviour.
    // NOTE: every consumer only reads `snomedLabels[code]`; none enumerate or spread the result.
    const agensOverlay: Record<string, string> = {};
    agens.forEach((v) => {
      agensOverlay[v.code] = agensOverlay[v.code] || v.label;
    });
    const has = (obj: Record<string, string>, key: string) => Object.prototype.hasOwnProperty.call(obj, key);
    return new Proxy({} as Record<string, string>, {
      get(_target, prop) {
        if (typeof prop !== 'string') return undefined;
        // Mirror the original `{...english, ...locale}` precedence exactly: a present locale key wins
        // even when its value is '' (locale was spread last), and the agens overlay fills in only when
        // the resulting value is falsy.
        const hasLocale = has(localeSnomedLabels, prop);
        const hasEnglish = has(englishSnomedLabels, prop);
        const baseLabel = hasLocale ? localeSnomedLabels[prop] : englishSnomedLabels[prop];
        return baseLabel || agensOverlay[prop] || (hasLocale || hasEnglish ? '' : undefined);
      },
      has(_target, prop) {
        return typeof prop === 'string' && (has(localeSnomedLabels, prop) || has(englishSnomedLabels, prop) || has(agensOverlay, prop));
      },
    });
  }, [englishSnomedLabels, localeSnomedLabels, agens]);
  return {snomedLabels, snomedLabelsLoading: localeLoading || englishLoading};
}
/**
 * Returns SNOMED labels only for the supplied `codes` (pathogens, antibiotics, or anything else that
 * carries a SNOMED code). Builds a small, stable map memoized on the set of codes, so consumers get a
 * subset they can safely pass as a prop / hook dependency without dragging the full (~tens of
 * thousands of entries) dictionary around. Codes with no label are omitted (so `subset[code] ?? code`
 * still works). Backed by {@link useSnomedLabels}.
 */
export function useSnomedLabelsByCode(
  config: ConfigObjectType,
  codes: ReadonlyArray<number | string> = []
): {snomedLabels: Record<string, string>; snomedLabelsLoading: boolean} {
  const {snomedLabels, snomedLabelsLoading} = useSnomedLabels(config);
  // Stable primitive key: a fresh `codes` array identity on every render must not rebuild the subset.
  const codesKey = codes.map((code) => String(code)).join('|');
  const subset = useMemo(() => {
    const acc: Record<string, string> = {};
    codes.forEach((code) => {
      const key = String(code);
      const label = snomedLabels[key];
      if (label !== undefined) acc[key] = label;
    });
    return acc;
    // `codesKey` captures the code set; `snomedLabels` is already memoized inside useSnomedLabels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snomedLabels, codesKey]);
  return {snomedLabels: subset, snomedLabelsLoading};
}
export const HaidiAtbSusceptibilityFormatter = React.memo<Props>((props) => {
  const {value, data, underlined, noWrap, style, snomedLabels, snomedLabelsLoading} = props;
  const colors = getAtbSusceptibilityColor(data);
  return (
    <div
      data-name='atb-formatter'
      css={styles.chip}
      style={{
        color: colors?.text,
        background: colors?.background,
        textDecoration: underlined ? 'underline' : undefined,
        ...(style ?? {}),
      }}
    >
      <div css={styles.horizontalPadding} />
      <span
        css={styles.text}
        style={
          noWrap
            ? {
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        {snomedLabels[value] || (snomedLabelsLoading ? '' : value)}
      </span>
      <div css={styles.horizontalPadding} />
    </div>
  );
});
type ListProps = {
  value: {
    atbSusceptibility: HaidiSchema.components['schemas']['LabAtbSusceptibilityInterpretationCodeEnum'];
    atbSnomedCode: number | string;
  }[];
  snomedLabels: Record<string, string>;
  snomedLabelsLoading: boolean;
};
export const HaidiAtbSusceptibilityListFormatter: React.FC<ListProps> = (props) => {
  const {value, snomedLabels, snomedLabelsLoading} = props;
  const sorted = React.useMemo(
    () =>
      sortBy(value ?? [], (v) => {
        let susceptibilityKey = 0;
        switch (v.atbSusceptibility) {
          case 'R':
            susceptibilityKey = 0;
            break;
          case 'I':
            susceptibilityKey = 1;
            break;
          case 'S':
            susceptibilityKey = 2;
            break;
          case 'IE':
            susceptibilityKey = 3;
            break;
        }
        const snomedLabel = snomedLabels[v.atbSnomedCode] || v.atbSnomedCode;
        return [susceptibilityKey, snomedLabel];
      }),
    [value, snomedLabels]
  );
  return (
    <>
      {sorted.map((v, i) => (
        <HaidiAtbSusceptibilityFormatter
          key={i}
          {...AG_GRID_COMPATIBILITY}
          data={v}
          value={v.atbSnomedCode}
          snomedLabels={snomedLabels}
          snomedLabelsLoading={snomedLabelsLoading}
        />
      ))}
    </>
  );
};

const ATB_SUSCEPTIBILITY_COLOR_MAP: Record<string, {text: string; background: string}> = {
  S: {text: 'var(--dlTheme-700)', background: '#CDFBE0'}, // Susceptible
  I: {text: 'var(--dlTheme-700)', background: '#EDFFA5'}, // Intermediate
  R: {text: 'var(--dlTheme-700)', background: '#FFD6D6'}, // Resistant
  IE: {text: 'var(--dlTheme-700)', background: '#E2E8F2'}, // Insufficient evidence
};
export function getAtbSusceptibilityColor(data) {
  return ATB_SUSCEPTIBILITY_COLOR_MAP?.[data.atbSusceptibility] ?? {};
}

HaidiAtbSusceptibilityFormatter.displayName = 'HaidiAtbSusceptibilityFormatter';

// Static styles — no theme dependency, so compute once at module load instead of per cell render.
const styles = {
  chip: css`
    max-width: 100%;
    border-radius: 4px;
    padding: 3px 0;
    display: inline-flex;
    justify-content: flex-start;
    align-items: flex-start;
  `,
  horizontalPadding: css`
    width: 8px;
    min-width: 8px;
    height: 1em;
    min-height: 1em;
  `,
  text: css`
    border-radius: 4px;
    margin: 0;
    font-family: SourceSansPro-Regular;
    font-size: 12px;
    line-height: 18px;
    text-wrap: wrap;
    word-break: break-word;
    text-align: left;
  `,
};
